import React, { useState, useEffect } from "react";
import { Avatar, Typography, Box, Paper, IconButton } from "@mui/material";
import { UserIcon, EnvelopeIcon, BuildingOfficeIcon, BriefcaseIcon, PencilIcon, IdentificationIcon, CheckIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useSelector, useDispatch } from "react-redux";
import { stringToColor, getInitial } from "../../utils/string";
import { updateUserInfo as updateUserInfoAction } from '../../features/auth/authSlice';
import PageHeader from '../Header/PageHeader';
import ConfirmationModal from "../Modal/ConfirmationModal";
import ButtonComponent from '../customButton';
import InputBox from '../FormComponent/InputBox';
import { UserRoleTypes } from "../../enums/userRole";
import { useSnackbar } from "../../utils/useSnackbar";
import ChipComponent from '../chips/ChipComponent/ChipComponent';
import { getUserRoleVariant } from '../chips/ChipComponent/chipVariants';
import styles from '../../styles/profilePage/index.module.scss';
import stylesCommon from '../../styles/component.module.scss';
import { updateUserInfo } from '../../utils/api/user_apis';
import { getOrganizations } from '../../utils/api/organization_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import DEFAULT_ROUTES from '../../constants/routes';
import { dateFormat } from '../../utils/date';
import { CONSENT_COPY } from '../../constants/consentCopy';
import LegalDocumentLink from '../consent/LegalDocumentLink';

const DetailItem = ({ label, value, icon }) => (
  <Box className={styles.detailItem}>
    <Box className={styles.detailItemHeader}>
      {icon && <Box>{icon}</Box>}
      <Typography variant="body2" className={styles.detailItemLabel}>
        {label}
      </Typography>
    </Box>
    <Typography variant="body1" className={styles.detailItemValue}>
      {value}
    </Typography>
  </Box>
);

const AccountTab = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const auth = useSelector((state) => state.auth);
  const [allorg, setallOrg] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const iconColor = styles.iconColor;

  const getDataOrDefault = (data, defaultValue = 'No Data') => {
    return data != null ? data : defaultValue;
  };

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const response = await getOrganizations();
        if (response.status === STATUS.SUCCESS) {
          setallOrg(response.data);
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
      }
    };
    fetchOrg();
  }, []);

  useEffect(() => {
    if (isEditing) {
      setUsername(getDataOrDefault(auth.user?.username));
      setUserEmail(getDataOrDefault(auth.user?.user_email));
    }
  }, [isEditing, auth.user]);

  const handleEditToggle = () => setIsEditing(!isEditing);

  const handleSave = async () => {
    if (!username) {
      showSnackbar("Username cannot be empty", "error");
      return;
    }
    try {
      const response = await updateUserInfo({ username });
      if (response.status === STATUS.SUCCESS) {
        dispatch(updateUserInfoAction({ username: response.data.username, email: response.data.user_email }));
        showSnackbar(response.message || "User Details updated successfully!", "success");
        setIsEditing(false);
      } else {
        showSnackbar(response.error || "Error updating user details", "error");
      }
    } catch (error) {
      showSnackbar(error.error || "Error updating user details", "error");
    }
  };

  const handleCancel = () => setIsEditing(false);
  const openConfirmationModal = () => setOpenModal(true);
  const handleConfirmSave = () => {
    handleSave();
    setOpenModal(false);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    handleCancel();
  };

  const createAvatarWithInitials = (name) => {
    const initials = getInitial(name);
    return (
      <Avatar
        className={styles.avatar}
        sx={{ bgcolor: name ? stringToColor(name) : iconColor }}
      >
        {initials}
      </Avatar>
    );
  };

  const orgDetails = [
    { label: "Company Name", value: getDataOrDefault(allorg[0]?.name), icon: <BuildingOfficeIcon style={{ width: 20, height: 20, color: iconColor }} /> },
    { label: "Organization ID", value: getDataOrDefault(allorg[0]?.id), icon: <IdentificationIcon style={{ width: 20, height: 20, color: iconColor }} /> },
  ];

  const consent = auth.user?.consent;

  const getLegalStatus = (documentConsent) => {
    if (!documentConsent) {
      return CONSENT_COPY.statusUnavailable;
    }
    return documentConsent.needs_reconsent
      ? CONSENT_COPY.statusUpdateRequired
      : CONSENT_COPY.statusUpToDate;
  };

  const legalDetails = [
    {
      label: "Terms of Service",
      acceptedOn: consent?.tos?.accepted_at
        ? dateFormat(consent.tos.accepted_at)
        : CONSENT_COPY.acceptanceNotRecorded,
      status: getLegalStatus(consent?.tos),
      link: DEFAULT_ROUTES.TERMS,
    },
    {
      label: "Privacy Policy",
      acceptedOn: consent?.privacy?.accepted_at
        ? dateFormat(consent.privacy.accepted_at)
        : CONSENT_COPY.acceptanceNotRecorded,
      status: getLegalStatus(consent?.privacy),
      link: DEFAULT_ROUTES.PRIVACY,
    },
  ];

  return (
    <Box className={stylesCommon['cover-container-row']}>
      <Box className={styles.headerSection}>
        <Box className={styles.avatarContainer}>
          {createAvatarWithInitials(getDataOrDefault(auth.user?.username))}
        </Box>
        <Box className={styles.userInfoContainer}>
          <Typography variant="h6" className={styles.userName}>
            {getDataOrDefault(auth.user?.username)}
          </Typography>
        </Box>
      </Box>

      <Box className={styles.gridContainer}>
        <Paper elevation={2} className={styles.paperContainer}>
          <Box className={styles.paperHeader}>
            <Typography variant="heading1">User Details</Typography>
            <Box>
              {isEditing ? (
                <Box className={styles.editButtonContainer}>
                  <ButtonComponent variant="outline" onClick={handleCancel} ctaText="Cancel" className="slate600" />
                  <ButtonComponent variant="primary" onClick={openConfirmationModal} ctaText="Save" startIcon={<CheckIcon className="icon white" />} className="white bg-blue600" />
                </Box>
              ) : (
                <IconButton onClick={handleEditToggle}>
                  <PencilIcon className="icon" />
                </IconButton>
              )}
            </Box>
          </Box>
          <Box className={styles.detailItem}>
            <Box className={styles.detailItem}>
              <Box className={styles.detailItemHeader}>
                <UserIcon style={{ width: 20, height: 20, color: iconColor }} />
                <Typography variant="body2" className={styles.detailItemLabel}>Full Name</Typography>
              </Box>
              <Typography variant="body1" className={styles.detailItemValue}>
                {isEditing ? (
                  <InputBox value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Enter your full name" variant="outlined" size="small" />
                ) : (
                  getDataOrDefault(auth.user?.username)
                )}
              </Typography>
            </Box>
            <Box className={styles.detailItem}>
              <Box className={styles.detailItemHeader}>
                <EnvelopeIcon style={{ width: 20, height: 20, color: iconColor }} />
                <Typography variant="body2" className={styles.detailItemLabel}>Email Address</Typography>
              </Box>
              <Typography variant="body1" className={styles.detailItemValue}>
                {isEditing ? (
                  <InputBox value={userEmail} placeholder="Email address" variant="outlined" size="small" disabled={true} type="email" />
                ) : (
                  getDataOrDefault(auth.user?.user_email)
                )}
              </Typography>
            </Box>
            <Box className={styles.detailItem}>
              <Box className={styles.detailItemHeader}>
                <BriefcaseIcon style={{ width: 20, height: 20, color: iconColor }} />
                <Typography variant="body2" className={styles.detailItemLabel}>Role</Typography>
              </Box>
              <Box className={styles.roleChipsContainer}>
                {auth.user?.roles?.map((role, index) => {
                  const roleLabel = UserRoleTypes[role] || role;
                  return (
                    <ChipComponent key={index} variant={getUserRoleVariant(role)} size="small">
                      {roleLabel}
                    </ChipComponent>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={2} className={styles.paperContainer}>
          <Typography variant="heading1">Organization Details</Typography>
          <Box className={styles.organizationDetails}>
            {orgDetails.map((detail, index) => (
              <DetailItem key={index} label={detail.label} value={detail.value} icon={detail.icon} />
            ))}
          </Box>
        </Paper>

        <Paper elevation={2} className={`${styles.paperContainer} ${styles.paperContainerCompact}`}>
          <Typography variant="heading1">Legal</Typography>
          <Box className={styles.legalDetailsRow}>
            {legalDetails.map((detail, index) => (
              <Box key={index} className={styles.legalDetailCard}>
                <Box className={styles.detailItemHeader}>
                  <DocumentTextIcon style={{ width: 20, height: 20, color: iconColor }} />
                  <LegalDocumentLink to={detail.link}>{detail.label}</LegalDocumentLink>
                </Box>
                <Box className={styles.legalDetailMeta}>
                  <Typography variant="body2" className={styles.legalDetailMetaLine}>
                    Accepted on: {detail.acceptedOn}
                  </Typography>
                  <Typography variant="body2" className={styles.legalDetailMetaLine}>
                    Status: {detail.status}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      <ConfirmationModal
        open={openModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSave}
        title="Confirm Profile Update"
        message="This action will update your details in all the records. Are you sure you want to save these changes?"
      />
    </Box>
  );
};

export default AccountTab;
